import dayjs, { Dayjs } from 'dayjs';
import {
  BankingCurrency,
  BankingDetails,
  GlobalAccount,
  PaymentDetail,
  Recipient,
  SupportedFeature,
  TransferFeesResponse,
  TransferValues,
} from '../types/banking';

export type AggregatedInfo = {
  totalBalance: number;
  accounts: {
    name: string;
    balance: number;
    currency: string;
  }[];
};

export function formatBankingDetails(data: BankingDetails): AggregatedInfo {
  return {
    totalBalance: data.aggregatedBalance!,
    accounts: data.accountBalances
      ? data.accountBalances.map(({ currency, availableAmount }) => ({
          currency,
          name: currency,
          balance: availableAmount,
        }))
      : [],
  };
}

// Define SEPA countries (EU member states + EEA countries + Switzerland)
const SEPA_COUNTRIES = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
  // EEA countries
  'IS',
  'LI',
  'NO',
  // Additional SEPA countries
  'CH',
  'MC',
  'SM',
  'VA',
];

export function formatRecipientPayload(values: any): object {
  const nickname = values.nickname || values.beneficiary?.bank_details?.account_name;

  return {
    ...values,
    nickname,
  };
}

// Airwallex models the EU as `DK` (Denmark) for global-account country codes,
// but UI surfaces the EU flag. Single source of truth for that mapping.
export const getCountryFlagCode = (countryCode?: string): string => {
  if (!countryCode) return '';
  return countryCode === 'DK' ? 'EU' : countryCode;
};

// Static catalogue of Marco-supported (country, main currency) pairs for views
// that need a synchronous list before the creation-options endpoint resolves
// (filter dropdowns, currency menus). Keep aligned with the backend
// `SUPPORTED_GLOBAL_ACCOUNT_COUNTRIES` / `AW_GA_COUNTRY_TO_CURRENCY_MAPPING`.
export const SUPPORTED_GLOBAL_ACCOUNT_COUNTRIES: {
  code: string;
  currency: BankingCurrency;
}[] = [
  { code: 'AU', currency: 'AUD' },
  { code: 'DK', currency: 'EUR' },
  { code: 'HK', currency: 'HKD' },
  { code: 'MX', currency: 'MXN' },
  { code: 'NZ', currency: 'NZD' },
  { code: 'SG', currency: 'SGD' },
  { code: 'AE', currency: 'AED' },
  { code: 'GB', currency: 'GBP' },
  { code: 'US', currency: 'USD' },
  { code: 'CA', currency: 'CAD' },
  { code: 'PH', currency: 'PHP' },
];

export const getCurrenciesFromGlobalAccount = (
  globalAccount: GlobalAccount
): [BankingCurrency | null, BankingCurrency[]] => {
  if (!globalAccount?.currencies?.length) {
    return [null, []];
  }
  const mainCurrency = globalAccount.mainCurrency;
  const restOfCurrencies = globalAccount.currencies.filter((currency) => currency !== mainCurrency);
  return [mainCurrency, restOfCurrencies];
};

export const getRoutingCodesFromGlobalAccount = (
  globalAccount: GlobalAccount,
  currency: BankingCurrency
): { type: string; value: string }[] => {
  // Distinct routing codes for the given currency.
  const routingCodes = globalAccount.routingCodes
    .filter((routingCode) => routingCode.currency === currency)
    .reduce<{ [type: string]: { type: string; value: string } }>((acc, routingCode) => {
      if (!acc[routingCode.type]) {
        acc[routingCode.type] = { type: routingCode.type, value: routingCode.value };
      }
      return acc;
    }, {});
  return Object.values(routingCodes);
};

// Group routing codes into a SupportedFeature[] (one entry per unique
// (currency, type)) so TransferMethods — which still consumes the Airwallex
// supportedFeatures shape — keeps working off the local model.
export const getTransferMethodsForCurrency = (
  globalAccount: GlobalAccount,
  currency: BankingCurrency
): SupportedFeature[] => {
  const grouped: Record<string, SupportedFeature> = {};
  globalAccount.routingCodes
    .filter((rc) => rc.currency === currency)
    .forEach((rc) => {
      if (!grouped[rc.type]) {
        grouped[rc.type] = {
          currency,
          routingCodes: [],
          localClearingSystem: rc.type === 'swift' ? undefined : rc.type.toUpperCase(),
          transferMethod: rc.type === 'swift' ? 'SWIFT' : 'LOCAL',
          type: rc.type,
        };
      }
      grouped[rc.type].routingCodes.push({ type: rc.type, value: rc.value });
    });
  return Object.values(grouped);
};

export const getCardholderIndividualEmail = (email?: string) => {
  if (!email) {
    return email;
  }
  const [username, domain] = email.split('@');
  return `${username}+INDIVIDUAL@${domain}`;
};

export const calculateConversion = (
  values: TransferValues,
  rateFields: {
    strongCurrency: BankingCurrency;
    rate: number;
  },
  calculateToField: 'to' | 'from'
): TransferValues => {
  const exchangeRate =
    values.fromCurrency === rateFields.strongCurrency ? rateFields.rate : 1 / rateFields.rate;
  const valuesCopy = { ...values };

  if (calculateToField === 'to') {
    valuesCopy.to = values.from * exchangeRate;
  } else {
    valuesCopy.from = values.to / exchangeRate;
  }

  return valuesCopy;
};

export const calculateTransferValues = (
  props: TransferValues,
  changedField: 'from' | 'to' | 'fromCurrency',
  calculateField: 'to' | 'from',
  transferFees: TransferFeesResponse
): TransferValues => {
  const strongCurrency = transferFees.conversionRate?.currencyPair?.substring(0, 3) as BankingCurrency;
  const rate = transferFees.conversionRate?.rate as number;
  if (changedField === 'fromCurrency' && props.fromCurrency !== props.toCurrency) {
    return calculateConversion(props, { strongCurrency, rate }, calculateField);
  } else if (changedField === 'fromCurrency' && props.fromCurrency === props.toCurrency) {
    return {
      from: props.from,
      fromCurrency: props.fromCurrency,
      to: props.to,
      toCurrency: props.toCurrency,
    };
  } else if ((changedField === 'from' || changedField === 'to') && props.fromCurrency !== props.toCurrency) {
    return calculateConversion(props, { strongCurrency, rate }, calculateField);
  } else if (changedField === 'from' && props.fromCurrency === props.toCurrency) {
    return {
      from: props.from,
      fromCurrency: props.fromCurrency,
      to: props.from,
      toCurrency: props.toCurrency,
    };
  } else if (changedField === 'to' && props.fromCurrency === props.toCurrency) {
    return {
      from: props.to,
      fromCurrency: props.fromCurrency,
      to: props.to,
      toCurrency: props.toCurrency,
    };
  }
  return props;
};

export function getNextBusinessDay(from: Dayjs): Dayjs {
  let next = from.add(1, 'day');
  while (next.day() === 0 || next.day() === 6) {
    next = next.add(1, 'day');
  }
  return next;
}

/** True when scheduled date is on or after the next business day from today (for lock-rate / prepayment UI). */
export function isScheduledOnOrAfterNextBusinessDay(scheduled: Dayjs): boolean {
  const nextBusinessDay = getNextBusinessDay(dayjs());
  return !scheduled.isBefore(nextBusinessDay, 'day');
}

export const purposeOptions = [
  {
    value: 'audio_visual_services',
    label: 'banking-page:transfers.transferPurposeOptions.audio_visual_services',
  },
  { value: 'bill_payment', label: 'banking-page:transfers.transferPurposeOptions.bill_payment' },
  { value: 'business_expenses', label: 'banking-page:transfers.transferPurposeOptions.business_expenses' },
  { value: 'construction', label: 'banking-page:transfers.transferPurposeOptions.construction' },
  {
    value: 'donation_charitable_contribution',
    label: 'banking-page:transfers.transferPurposeOptions.donation_charitable_contribution',
  },
  { value: 'education_training', label: 'banking-page:transfers.transferPurposeOptions.education_training' },
  { value: 'freight', label: 'banking-page:transfers.transferPurposeOptions.freight' },
  { value: 'goods_purchased', label: 'banking-page:transfers.transferPurposeOptions.goods_purchased' },
  { value: 'investment_capital', label: 'banking-page:transfers.transferPurposeOptions.investment_capital' },
  {
    value: 'investment_proceeds',
    label: 'banking-page:transfers.transferPurposeOptions.investment_proceeds',
  },
  { value: 'living_expenses', label: 'banking-page:transfers.transferPurposeOptions.living_expenses' },
  {
    value: 'loan_credit_repayment',
    label: 'banking-page:transfers.transferPurposeOptions.loan_credit_repayment',
  },
  { value: 'medical_services', label: 'banking-page:transfers.transferPurposeOptions.medical_services' },
  { value: 'pension', label: 'banking-page:transfers.transferPurposeOptions.pension' },
  {
    value: 'professional_business_services',
    label: 'banking-page:transfers.transferPurposeOptions.professional_business_services',
  },
  { value: 'real_estate', label: 'banking-page:transfers.transferPurposeOptions.real_estate' },
  { value: 'taxes', label: 'banking-page:transfers.transferPurposeOptions.taxes' },
  { value: 'technical_services', label: 'banking-page:transfers.transferPurposeOptions.technical_services' },
  {
    value: 'transfer_to_own_account',
    label: 'banking-page:transfers.transferPurposeOptions.transfer_to_own_account',
  },
  { value: 'travel', label: 'banking-page:transfers.transferPurposeOptions.travel' },
  { value: 'wages_salary', label: 'banking-page:transfers.transferPurposeOptions.wages_salary' },
  { value: 'other_services', label: 'banking-page:transfers.transferPurposeOptions.other_services' },
];

export enum PaymentSystem {
  SWIFT_OUR = 'SWIFT_OUR',
  SWIFT_SHA = 'SWIFT_SHA',
  MARCO_WTW = 'MARCO_WTW',
  SEPA = 'SEPA',
  FASTER_PAYMENTS = 'FASTER_PAYMENTS',
  ACH = 'ACH',
  BANK_TRANSFER = 'BANK_TRANSFER',
  LOCAL = 'LOCAL',
  RTGS = 'RTGS',
  FPS = 'FPS',
  GIRO = 'GIRO',
}

export const getPaymentSystem = (recipient: Recipient, coverSwiftFees: boolean): PaymentSystem | string => {
  const transferMethod = recipient.transferMethods[0];
  const { bankDetails } = recipient.beneficiary;

  // Handle SWIFT transfers
  if (transferMethod === 'SWIFT') {
    return coverSwiftFees ? PaymentSystem.SWIFT_OUR : PaymentSystem.SWIFT_SHA;
  }

  // Handle LOCAL transfers
  if (transferMethod === 'LOCAL') {
    if (!bankDetails) {
      return PaymentSystem.MARCO_WTW;
    }

    // Check for SEPA eligibility first
    if (SEPA_COUNTRIES.includes(bankDetails.bankCountryCode) && bankDetails.accountCurrency === 'EUR') {
      return PaymentSystem.SEPA;
    }

    // If there's a localClearingSystem defined, use that
    if (bankDetails.localClearingSystem) {
      return bankDetails.localClearingSystem;
    }

    // Handle country-specific payment systems
    switch (bankDetails.bankCountryCode) {
      case 'GB':
        return PaymentSystem.FASTER_PAYMENTS;
      case 'US':
        // For US, default to ACH if no specific clearing system is defined
        return PaymentSystem.ACH;
      case 'AU':
        return PaymentSystem.BANK_TRANSFER;
      case 'CA':
        return PaymentSystem.LOCAL; // Default to LOCAL for Canada if no specific system defined
      case 'HK':
        // For Hong Kong, check if it's a USD/EUR transfer which should use RTGS
        if (bankDetails.accountCurrency === 'USD' || bankDetails.accountCurrency === 'EUR') {
          return PaymentSystem.RTGS;
        }
        return PaymentSystem.FPS; // Default to FPS for other currencies
      case 'SG':
        return PaymentSystem.GIRO; // Default to GIRO for Singapore if no specific system defined
    }

    // If no specific country rules match, keep it as LOCAL
    return PaymentSystem.LOCAL;
  }

  // For any other transfer method, return it as is
  return transferMethod;
};

export function formatPaymentExchangeRate(details: PaymentDetail): string {
  const { conversion } = details;
  if (!conversion?.currencyPair || !conversion.rate) {
    return '-';
  }
  return `1 ${conversion.currencyPair.substring(0, 3)} = ${
    conversion.rate
  } ${conversion.currencyPair.substring(3)}`;
}
